import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { useInterval } from 'usehooks-ts';

import { notificationService } from '@arrived/common';
import { UserId } from '@arrived/models';
import { useGetCurrentUserQuery, usePostCreateMFAEnrollmentMutation } from '@arrived/queries';

const BLOCKED_SECONDS = 60;

export const useResendMFAEmail = (userId?: UserId) => {
  const [isResendMFABlocked, setIsResendMFABlocked] = useState(false);
  const [blockedCounter, setBlockedCounter] = useState(BLOCKED_SECONDS);
  const currentUserState = useGetCurrentUserQuery({ enabled: !userId });

  const { mutateAsync: enrollMFA, isLoading: enrollIsLoading } = usePostCreateMFAEnrollmentMutation({
    onSuccess: () => {
      notificationService.success(
        <FormattedMessage
          id="profile.security.items.mfa.enrollSuccess"
          defaultMessage="An email has been sent to complete MFA enrollment"
        />
      );
    },
    onError: () => {
      notificationService.error(
        <FormattedMessage
          id="profile.security.items.mfa.enrollError"
          defaultMessage="There was an issue with sending your MFA enrollment email"
        />
      );
    },
  });

  const handleResendMFAEmail = useCallback(() => {
    let passedUserId = userId;

    if (!passedUserId && currentUserState.isSuccess) {
      passedUserId = currentUserState.data.id;
    }

    if (passedUserId) {
      setIsResendMFABlocked(true);
      setBlockedCounter(BLOCKED_SECONDS);
      enrollMFA(passedUserId);
    }
  }, [currentUserState, enrollMFA, userId]);

  useInterval(
    () => {
      setBlockedCounter(blockedCounter - 1);
    },
    isResendMFABlocked ? 1000 : null
  );

  useEffect(() => {
    if (blockedCounter <= 0) {
      setBlockedCounter(BLOCKED_SECONDS);
      setIsResendMFABlocked(false);
    }
  }, [blockedCounter]);

  return { handleResendMFAEmail, isBlocked: isResendMFABlocked, counter: blockedCounter, enrollIsLoading };
};
